import {
  ACCOUNT_AVATAR_COLORS,
  FIXED_ACCOUNT_AVATARS,
  decodeAccountAvatarColor,
  decodeAccountAvatarReference,
  decodeDiscordAvatarUrl,
  isDiscordAvatarReference,
  isFixedAvatarReference,
} from "../account/avatar-reference.js";
import { translate } from "../account/identity-view-primitives.js";

export const ACCOUNT_AVATAR_TAG = "fishy-account-avatar";
export const ACCOUNT_AVATAR_PICKER_TAG = "fishy-account-avatar-picker";
const SEARCHABLE_DROPDOWN_TAG = "fishy-searchable-dropdown";

let pickerSequence = 0;

function avatarClasses(size) {
  if (size === "nav") {
    return Object.freeze({
      glyphFrame: "size-7 rounded-full inline-flex items-center justify-center ring-2 ring-base-100 data-[avatar-color=base]:bg-base-300 data-[avatar-color=base]:text-base-content data-[avatar-color=primary]:bg-primary data-[avatar-color=primary]:text-primary-content data-[avatar-color=secondary]:bg-secondary data-[avatar-color=secondary]:text-secondary-content data-[avatar-color=accent]:bg-accent data-[avatar-color=accent]:text-accent-content",
      icon: "size-4",
      imageFrame: "size-7 rounded-full overflow-hidden bg-base-200 ring-2 ring-base-100",
      skeletonFrame: "size-7 rounded-full skeleton inline-flex",
    });
  }
  if (size === "xs") {
    return Object.freeze({
      glyphFrame: "size-9 rounded-xl inline-flex items-center justify-center ring-2 ring-base-100 data-[avatar-color=base]:bg-base-300 data-[avatar-color=base]:text-base-content data-[avatar-color=primary]:bg-primary data-[avatar-color=primary]:text-primary-content data-[avatar-color=secondary]:bg-secondary data-[avatar-color=secondary]:text-secondary-content data-[avatar-color=accent]:bg-accent data-[avatar-color=accent]:text-accent-content",
      icon: "size-5",
      imageFrame: "size-9 rounded-xl overflow-hidden bg-base-200 ring-2 ring-base-100",
      skeletonFrame: "size-9 rounded-xl skeleton inline-flex",
    });
  }
  return size === "sm"
    ? Object.freeze({
        glyphFrame: "size-12 rounded-2xl inline-flex items-center justify-center ring-2 ring-base-100 data-[avatar-color=base]:bg-base-300 data-[avatar-color=base]:text-base-content data-[avatar-color=primary]:bg-primary data-[avatar-color=primary]:text-primary-content data-[avatar-color=secondary]:bg-secondary data-[avatar-color=secondary]:text-secondary-content data-[avatar-color=accent]:bg-accent data-[avatar-color=accent]:text-accent-content",
        icon: "size-6",
        imageFrame: "size-12 rounded-2xl overflow-hidden bg-base-200 ring-2 ring-base-100",
        skeletonFrame: "size-12 rounded-2xl skeleton inline-flex",
      })
    : Object.freeze({
        glyphFrame: "size-16 rounded-3xl inline-flex items-center justify-center ring-2 ring-base-100 data-[avatar-color=base]:bg-base-300 data-[avatar-color=base]:text-base-content data-[avatar-color=primary]:bg-primary data-[avatar-color=primary]:text-primary-content data-[avatar-color=secondary]:bg-secondary data-[avatar-color=secondary]:text-secondary-content data-[avatar-color=accent]:bg-accent data-[avatar-color=accent]:text-accent-content",
        icon: "size-8",
        imageFrame: "size-16 rounded-3xl overflow-hidden bg-base-200 ring-2 ring-base-100",
        skeletonFrame: "size-16 rounded-3xl skeleton inline-flex",
      });
}

function avatarPixels(size) {
  if (size === "nav") return 28;
  if (size === "xs") return 36;
  return size === "sm" ? 48 : 64;
}

export function accountAvatarExternalImageRequest(value, externalImagesAllowed) {
  return isDiscordAvatarReference(value) && externalImagesAllowed === true
    ? Object.freeze({ src: value, crossOrigin: "anonymous" })
    : null;
}

export function accountAvatarGlyphReference(value) {
  return isDiscordAvatarReference(value)
    ? "#fishy-user-4-fill"
    : value || "#fishy-user-4-fill";
}

function glyph(href, className) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", `fishy-icon shrink-0 ${className}`);
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
  use.setAttribute("width", "100%");
  use.setAttribute("height", "100%");
  use.setAttribute("href", href);
  svg.append(use);
  return svg;
}

const HTMLElementBase = globalThis.HTMLElement ?? class {};

export class FishyAccountAvatar extends HTMLElementBase {
  static get observedAttributes() {
    return ["color", "external-images-allowed", "loading", "size", "value"];
  }

  constructor() {
    super();
    this._value = "";
    this._color = "base";
    this._externalImagesAllowed = false;
    this._connected = false;
  }

  connectedCallback() {
    this._connected = true;
    this._render();
  }

  disconnectedCallback() {
    this._connected = false;
  }

  attributeChangedCallback(name, _oldValue, newValue) {
    if (name === "value") {
      this._value = decodeAccountAvatarReference(newValue ?? "");
    }
    if (name === "color") {
      this._color = decodeAccountAvatarColor(newValue ?? "");
    }
    if (name === "external-images-allowed") {
      this._externalImagesAllowed = newValue !== null;
    }
    if (this._connected) this._render();
  }

  get value() {
    return this._value;
  }

  set value(value) {
    this._value = decodeAccountAvatarReference(value);
    if (this.getAttribute?.("value") !== this._value) {
      this.setAttribute?.("value", this._value);
    } else if (this._connected) {
      this._render();
    }
  }

  get color() {
    return this._color;
  }

  set color(value) {
    this._color = decodeAccountAvatarColor(value);
    if (this.getAttribute?.("color") !== this._color) {
      this.setAttribute?.("color", this._color);
    } else if (this._connected) {
      this._render();
    }
  }

  get externalImagesAllowed() {
    return this._externalImagesAllowed;
  }

  set externalImagesAllowed(value) {
    this._externalImagesAllowed = value === true;
    this.toggleAttribute?.("external-images-allowed", this._externalImagesAllowed);
    if (this._connected) this._render();
  }

  _render() {
    const { glyphFrame, icon, imageFrame, skeletonFrame } = avatarClasses(
      this.getAttribute?.("size"),
    );
    if (this.hasAttribute?.("loading")) {
      const root = document.createElement("span");
      root.className = "avatar avatar-placeholder";
      root.setAttribute("aria-hidden", "true");
      const placeholder = document.createElement("span");
      placeholder.className = skeletonFrame;
      root.append(placeholder);
      this.replaceChildren(root);
      return;
    }
    const imageRequest = accountAvatarExternalImageRequest(
      this._value,
      this._externalImagesAllowed,
    );
    const root = document.createElement("span");
    root.className = imageRequest ? "avatar" : "avatar avatar-placeholder";
    root.setAttribute("aria-hidden", "true");
    const frameNode = document.createElement("span");
    frameNode.className = imageRequest ? imageFrame : glyphFrame;
    if (!imageRequest) {
      frameNode.dataset.avatarColor = isFixedAvatarReference(this._value)
        ? this._color
        : "base";
    }
    if (imageRequest) {
      const image = document.createElement("img");
      image.crossOrigin = imageRequest.crossOrigin;
      image.src = imageRequest.src;
      image.alt = "";
      image.width = avatarPixels(this.getAttribute?.("size"));
      image.height = image.width;
      image.loading = "eager";
      image.decoding = "async";
      frameNode.append(image);
    } else {
      frameNode.append(glyph(accountAvatarGlyphReference(this._value), icon));
    }
    root.append(frameNode);
    this.replaceChildren(root);
  }
}

function avatarPresentation(value, label, discordImagesAllowed = false, color = "base") {
  const content = document.createElement("span");
  content.className = "flex min-w-0 flex-1 items-center gap-3";
  const preview = document.createElement(ACCOUNT_AVATAR_TAG);
  preview.setAttribute("size", "xs");
  preview.setAttribute("value", value);
  preview.setAttribute("color", color);
  preview.externalImagesAllowed = discordImagesAllowed;
  const text = document.createElement("span");
  text.className = "min-w-0 truncate text-sm font-medium";
  text.textContent = label;
  content.append(preview, text);
  return content;
}

function avatarTemplate(option, role, discordImagesAllowed = false, color = "base") {
  const template = document.createElement("template");
  template.dataset.role = role;
  template.dataset.value = option.value;
  template.dataset.label = option.label;
  template.dataset.searchText = `${option.id} ${option.label}`;
  template.content.append(
    avatarPresentation(option.value, option.label, discordImagesAllowed, color),
  );
  return template;
}

function selectedBadge() {
  const badge = document.createElement("span");
  badge.className = "badge badge-ghost badge-sm shrink-0";
  badge.textContent = translate("common.status.selected", "Selected");
  return badge;
}

function avatarSelector(
  options,
  selectedValue,
  idPrefix,
  discordImagesAllowed = false,
  color = "base",
) {
  const selectedChoice = options.find(({ value }) => value === selectedValue);
  const selected = selectedChoice ?? {
    value: "",
    label: translate(
      "account.avatar.choose_fishystuff",
      "Choose a FishyStuff avatar",
    ),
  };
  const panelId = `${idPrefix}-panel`;
  const searchId = `${idPrefix}-search`;
  const dropdown = document.createElement(SEARCHABLE_DROPDOWN_TAG);
  dropdown.className = "relative block min-w-0 w-full";
  dropdown.dataset.accountAvatarSelector = "";
  dropdown.dataset.accountAvatarSource = "fishystuff";
  dropdown.dataset.accountAvatarSelected = String(Boolean(selectedChoice));
  dropdown.setAttribute("label", selected.label);
  dropdown.setAttribute("value", selected.value);
  dropdown.setAttribute("placeholder", translate("account.avatar.search", "Search avatars"));
  dropdown.setAttribute("panel-mode", "detached");
  dropdown.setAttribute("panel-placement", "overlay-anchor");
  dropdown.setAttribute("panel-min-width", "panel");
  dropdown.setAttribute("panel-width", "22rem");

  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "btn btn-sm h-auto min-h-12 w-full justify-between px-3 py-2 font-normal";
  trigger.dataset.role = "trigger";
  trigger.setAttribute("aria-haspopup", "listbox");
  trigger.setAttribute("aria-expanded", "false");
  trigger.setAttribute("aria-controls", panelId);
  trigger.setAttribute(
    "aria-label",
    translate("account.avatar.fishystuff", "FishyStuff avatars"),
  );
  const selectedContent = avatarPresentation(
    selected.value,
    selected.label,
    discordImagesAllowed,
    color,
  );
  selectedContent.dataset.role = "selected-content";
  trigger.append(selectedContent);
  if (selectedChoice) trigger.append(selectedBadge());
  trigger.append(glyph("#fishy-caret-down", "size-4 opacity-60"));

  const panel = document.createElement("div");
  panel.id = panelId;
  panel.className = "absolute left-0 top-0 z-50 w-full min-w-full max-w-full";
  panel.dataset.role = "panel";
  panel.hidden = true;
  const panelShell = document.createElement("div");
  panelShell.className = "grid min-w-full overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-lg";
  const searchLabel = document.createElement("label");
  searchLabel.className = "input input-sm m-2 w-auto";
  searchLabel.htmlFor = searchId;
  searchLabel.append(glyph("#fishy-search-field", "size-4 opacity-60"));
  const search = document.createElement("input");
  search.id = searchId;
  search.type = "search";
  search.className = "grow";
  search.autocomplete = "off";
  search.spellcheck = false;
  search.placeholder = translate("account.avatar.search", "Search avatars");
  search.setAttribute("aria-label", search.placeholder);
  search.dataset.role = "search-input";
  searchLabel.append(search);
  const results = document.createElement("ul");
  results.className = "menu menu-sm max-h-72 w-full overflow-y-auto p-2";
  results.dataset.role = "results";
  results.tabIndex = -1;
  panelShell.append(searchLabel, results);
  panel.append(panelShell);

  const catalog = document.createElement("div");
  catalog.dataset.role = "selected-content-catalog";
  catalog.hidden = true;
  for (const option of options) {
    catalog.append(
      avatarTemplate(option, "selected-content", discordImagesAllowed, color),
      avatarTemplate(option, "option-content", discordImagesAllowed, color),
    );
  }
  dropdown.append(trigger, panel, catalog);
  return dropdown;
}

function avatarChoiceButton(
  value,
  label,
  source,
  selected,
  discordImagesAllowed = false,
  color = "base",
) {
  const button = document.createElement("button");
  button.type = "button";
  button.value = value;
  button.className = "btn btn-sm h-auto min-h-12 w-full justify-between gap-3 px-3 py-2 font-normal";
  button.dataset.accountAvatarSource = source;
  button.setAttribute("aria-pressed", String(selected));
  button.append(avatarPresentation(value, label, discordImagesAllowed, color));
  if (selected) button.append(selectedBadge());
  return button;
}

export class FishyAccountAvatarPicker extends HTMLElementBase {
  constructor() {
    super();
    this._connected = false;
    this._value = "";
    this._color = "base";
    this._discordAvatarUrl = "";
    this._discordImagesAllowed = false;
    this._fieldName = "traits.avatar_url";
    this._colorFieldName = "traits.avatar_color";
    this._idPrefix = `account-avatar-${++pickerSequence}`;
    this._onChange = this._onChange.bind(this);
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this._connected = true;
    this.addEventListener("change", this._onChange);
    this.addEventListener("click", this._onClick);
    this._render();
  }

  disconnectedCallback() {
    this._connected = false;
    this.removeEventListener("change", this._onChange);
    this.removeEventListener("click", this._onClick);
  }

  get value() {
    return this._value;
  }

  set value(value) {
    this._value = decodeAccountAvatarReference(value);
    if (this._connected) this._render();
  }

  set fieldName(value) {
    if (typeof value !== "string" || value !== "traits.avatar_url") {
      throw new TypeError("Account avatar field name is invalid");
    }
    this._fieldName = value;
    if (this._connected) this._render();
  }

  get color() {
    return this._color;
  }

  set color(value) {
    this._color = decodeAccountAvatarColor(value);
    if (this._connected) this._render();
  }

  set colorFieldName(value) {
    if (typeof value !== "string" || value !== "traits.avatar_color") {
      throw new TypeError("Account avatar color field name is invalid");
    }
    this._colorFieldName = value;
    if (this._connected) this._render();
  }

  set discordAvatarUrl(value) {
    this._discordAvatarUrl = decodeDiscordAvatarUrl(value);
    if (this._connected) this._render();
  }

  set discordImagesAllowed(value) {
    this._discordImagesAllowed = value === true;
    if (this._connected) this._render();
  }

  _onChange(event) {
    const dropdown = event.target?.closest?.("[data-account-avatar-selector]");
    if (event.target !== dropdown || !dropdown || !this.contains(dropdown)) return;
    const value = decodeAccountAvatarReference(dropdown.value);
    if (!isFixedAvatarReference(value)) return;
    this._select(value);
  }

  _onClick(event) {
    const colorChoice = event.target?.closest?.("button[data-account-avatar-color]");
    if (colorChoice && this.contains(colorChoice)) {
      this._color = decodeAccountAvatarColor(colorChoice.value);
      this._render();
      return;
    }
    const choice = event.target?.closest?.("button[data-account-avatar-source]");
    if (!choice || !this.contains(choice)) return;
    this._select(choice.value);
  }

  _select(value) {
    this._value = decodeAccountAvatarReference(value);
    const hidden = this.querySelector("[data-account-avatar-input]");
    if (hidden) hidden.value = this._value;
    this._render();
  }

  _render() {
    const fieldset = document.createElement("fieldset");
    fieldset.className =
      "fieldset min-w-0 grid-cols-1 gap-3 rounded-box border border-base-300 bg-base-200/35 p-4";
    const legend = document.createElement("legend");
    legend.className = "fieldset-legend text-base";
    legend.textContent = translate("account.avatar.title", "Profile picture");
    const help = document.createElement("p");
    help.className = "min-w-0 text-sm leading-5 text-base-content/65";
    help.textContent = translate(
      "account.avatar.detail",
      "Choose your Discord picture or a FishyStuff avatar. It is saved with your profile.",
    );
    const options = FIXED_ACCOUNT_AVATARS.map((choice) => ({
      ...choice,
      label: translate(choice.labelKey, choice.fallbackLabel),
    }));
    const selector = avatarSelector(
      options,
      isFixedAvatarReference(this._value) ? this._value : "",
      this._idPrefix,
      this._discordImagesAllowed,
      this._color,
    );
    const discordChoice = this._discordAvatarUrl;
    const sources = document.createElement("div");
    sources.className = "grid min-w-0 gap-2";
    sources.append(selector);
    if (isFixedAvatarReference(this._value)) {
      const colorGroup = document.createElement("div");
      colorGroup.className = "grid min-w-0 gap-2 pt-1";
      colorGroup.dataset.accountAvatarColorGroup = "";
      const colorLabel = document.createElement("p");
      colorLabel.className = "text-xs font-medium text-base-content/65";
      colorLabel.textContent = translate("account.avatar.color", "Avatar color");
      const colors = document.createElement("div");
      colors.className = "grid grid-cols-2 gap-2 sm:grid-cols-4";
      for (const choice of ACCOUNT_AVATAR_COLORS) {
        const selected = choice.value === this._color;
        const button = document.createElement("button");
        button.type = "button";
        button.value = choice.value;
        button.className = selected
          ? "group btn btn-sm btn-active h-auto min-h-10 justify-start gap-2 px-2 font-normal"
          : "group btn btn-sm h-auto min-h-10 justify-start gap-2 px-2 font-normal";
        button.dataset.accountAvatarColor = choice.value;
        button.dataset.avatarColor = choice.value;
        button.setAttribute("aria-pressed", String(selected));
        const swatch = document.createElement("span");
        swatch.className = "size-4 shrink-0 rounded-full border border-base-content/15 group-data-[avatar-color=base]:bg-base-300 group-data-[avatar-color=primary]:bg-primary group-data-[avatar-color=secondary]:bg-secondary group-data-[avatar-color=accent]:bg-accent";
        swatch.setAttribute("aria-hidden", "true");
        const label = document.createElement("span");
        label.className = "min-w-0 truncate";
        label.textContent = translate(choice.labelKey, choice.fallbackLabel);
        button.append(swatch, label);
        if (selected) button.append(glyph("#fishy-check-badge-solid", "ml-auto size-4"));
        colors.append(button);
      }
      colorGroup.append(colorLabel, colors);
      sources.append(colorGroup);
    }
    if (discordChoice) {
      sources.append(avatarChoiceButton(
        discordChoice,
        translate("account.avatar.discord", "Discord"),
        "discord",
        isDiscordAvatarReference(this._value),
        this._discordImagesAllowed,
        this._color,
      ));
    }
    const none = avatarChoiceButton(
      "",
      translate("account.avatar.none", "No picture"),
      "none",
      this._value === "",
      this._discordImagesAllowed,
      this._color,
    );
    sources.append(none);

    const hidden = document.createElement("input");
    hidden.type = "hidden";
    hidden.name = this._fieldName;
    hidden.value = this._value;
    hidden.dataset.accountAvatarInput = "";
    const hiddenColor = document.createElement("input");
    hiddenColor.type = "hidden";
    hiddenColor.name = this._colorFieldName;
    hiddenColor.value = this._color;
    hiddenColor.dataset.accountAvatarColorInput = "";
    fieldset.append(legend, help, sources);
    this.replaceChildren(fieldset, hidden, hiddenColor);
  }
}

export function registerAccountAvatarPicker() {
  if (typeof globalThis.customElements === "undefined") return;
  if (!globalThis.customElements.get(ACCOUNT_AVATAR_TAG)) {
    globalThis.customElements.define(ACCOUNT_AVATAR_TAG, FishyAccountAvatar);
  }
  if (!globalThis.customElements.get(ACCOUNT_AVATAR_PICKER_TAG)) {
    globalThis.customElements.define(ACCOUNT_AVATAR_PICKER_TAG, FishyAccountAvatarPicker);
  }
}
